import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker, Flex, Form, Input, InputNumber, Select } from 'antd';
import type { Dayjs } from 'dayjs';
import type { ContactRes } from 'api/swagger';
import { WHTaskType } from 'types/wh-task';
import { CargoItemInputFieldsSection } from 'components/common/cargo-item-input-fields-section';
import { ClientFormSelect } from 'components/common/client-form-select';
import { ContactFormSelect } from 'components/common/contact-form-select';
import { DataRangeFormPicker } from 'components/common/data-range-form-picker';
import { FormCurrencySelect } from 'components/common/form-currency-select/form-currency-select';
import { WarehouseFormSelect } from 'components/common/warehouse-form-select';
import { CreateContact } from 'components/contacts/create-contact';
import type { IWhTaskFormProps } from './types';
import { validationRules } from './validators';
import styles from './wh-task-form.module.scss';

export const WhTaskForm = (props: IWhTaskFormProps) => {
  const {
    form,
    formId,
    onFinish,
    initialValues,
    withFines = false,
    withoutWarehouseTaskItems = false,
    disableClientInput = false,
    withRealDates = false,
  } = props;
  const { t } = useTranslation();

  const [clientId, setClientId] = useState<number | undefined>(undefined);

  const type = Form.useWatch('type', form);

  const handleClientChange = (option: { value: number; label: string }) => {
    form.setFieldValue('contact', undefined);
    form.setFieldValue('client', { value: option.value, label: option.label });
    setClientId(Number(option.value));
  };

  const handleContactChange = (option: { value: string; label: string }) => {
    form.setFieldValue('contact', { value: option.value, label: option.label });
  };

  const handleCreteContact = (contact: ContactRes) => {
    form.setFieldValue('contact', { value: contact.id, label: contact.name });
    form.validateFields(['contact']);
  };

  const handleTypeChange = () => {
    form.setFieldsValue({ plateNumberFrom: undefined, plateNumberTo: undefined, date: undefined });
  };

  const handleDateChange = (date: Dayjs | null) => {
    form.setFieldValue('date', date ?? undefined);
  };

  const realArrivalDate = Form.useWatch('realArrivalDate', form);
  const realDepartureDate = Form.useWatch('realDepartureDate', form);

  const disabledArrivalDate = (current: Dayjs) => {
    if (!realDepartureDate) return false;
    return current.isAfter(realDepartureDate, 'day');
  };

  const disabledDepartureDate = (current: Dayjs) => {
    if (!realArrivalDate) return false;
    return current.isBefore(realArrivalDate, 'day');
  };

  const handleArrivalDateChange = (date: Dayjs | null) => {
    if (date && realDepartureDate && date.isAfter(realDepartureDate)) {
      form.setFieldValue('realDepartureDate', null);
    }
  };

  const handleDepartureDateChange = (date: Dayjs | null) => {
    if (date && realArrivalDate && date.isBefore(realArrivalDate)) {
      form.setFieldValue('realArrivalDate', null);
    }
  };

  useEffect(() => {
    if (!initialValues) {
      form.setFieldValue('type', WHTaskType.storing);
    } else if (initialValues.client) {
      setClientId(initialValues.client.value);
    }
  }, [form, initialValues]);

  const isStoringTaskType = type === WHTaskType.storing;

  return (
    <Form form={form} id={formId} layout='vertical' initialValues={initialValues} onFinish={onFinish}>
      <Flex justify='space-between' gap={12} flex={1} className={styles.flexContainer}>
        <Form.Item name='type' label={t('common:type')} rules={validationRules.type}>
          <Select placeholder={t('common:type')} defaultValue={WHTaskType.storing} onChange={handleTypeChange}>
            <Select.Option value={WHTaskType.storing}>{WHTaskType.storing}</Select.Option>
            <Select.Option value={WHTaskType.reloading}>{WHTaskType.reloading}</Select.Option>
          </Select>
        </Form.Item>
        <ClientFormSelect
          label={t('client:client')}
          name='client'
          placeholder={t('client:client')}
          rules={validationRules.client}
          handleClientChange={handleClientChange}
          disabled={disableClientInput}
        />
        <div>
          <ContactFormSelect
            label={t('contact:contact')}
            name='contact'
            placeholder={t('contact:contact')}
            rules={validationRules.contact}
            clientId={clientId}
            handleContactChange={handleContactChange}
            className={styles.withoutMargin}
          />
          {clientId ? (
            <CreateContact
              clientId={clientId}
              bthLabel={t('contact:create_new_contact')}
              textBtn
              onCreate={handleCreteContact}
            />
          ) : null}
        </div>
        <Form.Item
          label={t('client:client_order_number')}
          name='clientOrderNumber'
          rules={validationRules.clientOrderNumber}>
          <Input placeholder={t('client:client_order_number')} />
        </Form.Item>
      </Flex>
      <Flex justify={isStoringTaskType ? 'flex-start' : 'space-between'} gap={12} className={styles.flexContainer}>
        <WarehouseFormSelect
          name='warehouseId'
          placeholder='Select warehouse'
          label='Warehouse'
          rules={validationRules.warehouseId}
          className={styles.flexChild}
          initialValue={initialValues?.warehouseId}
        />
        {isStoringTaskType ? (
          <DataRangeFormPicker
            name='date'
            label={'Arrival date / Departure date'}
            className={styles.dateRangePicker}
            isRequired
          />
        ) : (
          <Form.Item name='date' label='Date' rules={validationRules.date} required>
            <DatePicker onChange={handleDateChange} />
          </Form.Item>
        )}
        {!isStoringTaskType && (
          <>
            <Form.Item name='plateNumberFrom' label='From plate number' rules={validationRules.plateNumberFrom}>
              <Input placeholder='From plate number' />
            </Form.Item>
            <Form.Item name='plateNumberTo' label='To plate number' rules={validationRules.plateNumberTo}>
              <Input placeholder='To plate number' />
            </Form.Item>
          </>
        )}
      </Flex>
      <Flex justify='flex-start' gap={18} className={styles.flexContainer}>
        <Form.Item label={t('order:freight')} name='freight' rules={validationRules.freight} className={styles.input}>
          <InputNumber placeholder={t('order:freight')} type='number' addonAfter={<FormCurrencySelect />} />
        </Form.Item>
        {withFines && (
          <>
            <Form.Item label={t('common:fine_to_us')} name='fineToUs' rules={validationRules.fineToUs}>
              <InputNumber placeholder={t('common:fine_to_us')} type='number' addonAfter={<FormCurrencySelect />} />
            </Form.Item>
            <Form.Item label={t('common:fine_by_us')} name='fineByUs' rules={validationRules.fineByUs}>
              <InputNumber placeholder={t('common:fine_by_us')} type='number' addonAfter={<FormCurrencySelect />} />
            </Form.Item>
          </>
        )}
      </Flex>
      <Form.Item
        name='totalWeight'
        rules={validationRules.totalWeight}
        label={t('cargo:total_weight_label')}
        className={styles.input}>
        <InputNumber placeholder={t('cargo:total_weight_label')} decimalSeparator={','} />
      </Form.Item>
      {withRealDates && isStoringTaskType && (
        <Flex gap={12} justify='flex-start' className={styles.flexContainer}>
          <Form.Item name='realArrivalDate' label={t('wh-task:actual_arrival_date')}>
            <DatePicker disabledDate={disabledArrivalDate} onChange={handleArrivalDateChange} />
          </Form.Item>
          <Form.Item name='realDepartureDate' label={t('wh-task:actual_departure_date')}>
            <DatePicker disabledDate={disabledDepartureDate} onChange={handleDepartureDateChange} />
          </Form.Item>
        </Flex>
      )}
      <Form.Item name='additionalInfo' label={t('common:additional_info')}>
        <Input.TextArea placeholder={t('common:additional_info')} />
      </Form.Item>
      {!withoutWarehouseTaskItems && <CargoItemInputFieldsSection form={form} name='warehouseTaskItems' withoutStack />}
    </Form>
  );
};
